import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Search, Upload, UserPlus, Mail, Phone, User, Edit2, Trash2, X, Download, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserManagement({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Employee',
    phone: '',
    designation: '',
    location: 'Office',
    manager: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, roleFilter, users]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(u =>
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  // Get list of managers and admins for dropdown
  const getManagersList = () => {
    return users.filter(u => u.role === 'Manager' || u.role === 'Admin');
  };

  // Helper to get manager name by email
  const getManagerName = (managerEmail) => {
    if (!managerEmail) return '-';
    const manager = users.find(u => u.email === managerEmail);
    return manager ? manager.name : managerEmail;
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/register`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Employee created successfully!');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', role: 'Employee', phone: '', designation: '', location: 'Office', manager: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const handleEditUser = (employee) => {
    setEditingUser(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      password: '',
      role: employee.role,
      phone: employee.phone || '',
      designation: employee.designation || '',
      location: employee.location || 'Office',
      manager: employee.manager || '',
    });
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: formData.name,
        role: formData.role,
        phone: formData.phone,
        designation: formData.designation,
        location: formData.location,
        manager: formData.manager,
      };
      
      // Only include password if provided
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      await axios.put(`${API}/users/${editingUser.email}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('Employee updated successfully!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update employee');
    }
  };

  const handleDeleteUser = async (email, name) => {
    if (!window.confirm(`Are you sure you want to delete ${name}? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(`${name} has been deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee');
    }
  };

  const handleBulkImport = () => {
    // Create file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          const text = await file.text();
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            toast.error('CSV file is empty or invalid');
            return;
          }
          
          // Parse CSV
          const headers = lines[0].split(',').map(h => h.trim());
          const employees = [];
          
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            const employee = {};
            headers.forEach((header, index) => {
              employee[header.toLowerCase()] = values[index] || '';
            });
            
            // Set default paid leaves based on location
            const location = employee.location || 'Office';
            const defaultLeaves = location === 'Remote' ? 8 : 10;
            
            employees.push({
              name: employee.name,
              email: employee.email,
              password: employee.password,
              role: employee.role || 'Employee',
              phone: employee.phone || '',
              designation: employee.designation || '',
              location: location,
              manager: employee.manager || '',
              paid_leave_balance: defaultLeaves,
            });
          }
          
          // Import employees
          const token = localStorage.getItem('token');
          let successCount = 0;
          let errorCount = 0;
          
          for (const emp of employees) {
            try {
              await axios.post(`${API}/auth/register`, emp, {
                headers: { Authorization: `Bearer ${token}` },
              });
              successCount++;
            } catch (error) {
              errorCount++;
              console.error(`Failed to create ${emp.email}:`, error);
            }
          }
          
          toast.success(`Imported ${successCount} employees successfully! ${errorCount > 0 ? `${errorCount} failed.` : ''}`);
          fetchUsers();
          
        } catch (error) {
          toast.error('Failed to process CSV file');
        }
      }
    };
    input.click();
  };

  const downloadSampleCSV = () => {
    const csvContent = `Name,Email,Password,Phone,Designation,Role,Location,Manager
John Doe,john.doe@company.com,Password123,555-0100,Software Engineer,Employee,Office,
Jane Smith,jane.smith@company.com,Password123,555-0101,Senior Developer,Manager,Remote,
Bob Johnson,bob.johnson@company.com,Password123,555-0102,DevOps Engineer,Employee,Office,jane.smith@company.com
Alice Williams,alice.williams@company.com,Password123,555-0103,Backend Developer,Employee,Remote,jane.smith@company.com`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Sample template downloaded!');
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Employee User Management</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">Create and manage employee accounts</p>
            </div>
            <div className="flex gap-2 md:gap-3">
              <button
                onClick={downloadSampleCSV}
                data-testid="download-template-button"
                className="hidden md:flex items-center gap-2 px-5 py-3 border border-input text-foreground rounded-lg hover:bg-muted transition-colors font-heading font-medium"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
              <button
                onClick={handleBulkImport}
                data-testid="bulk-import-button"
                className="hidden md:flex items-center gap-2 px-5 py-3 border-2 border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors font-heading font-semibold"
              >
                <Upload className="w-4 h-4" />
                Bulk CSV Import
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                data-testid="create-user-button"
                className="flex items-center gap-2 px-3 md:px-5 py-2 md:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-heading font-semibold shadow-sm text-sm md:text-base"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Create New Employee</span>
                <span className="sm:hidden">Add Employee</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {/* Search and Filters */}
          <div className="bg-white rounded-xl border border-border p-4 md:p-6 mb-6 md:mb-8 shadow-sm">
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  data-testid="search-users"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="role-filter"
                className="px-3 md:px-4 py-2 md:py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary sm:min-w-[150px]"
              >
                <option value="all">All Roles</option>
                <option value="Employee">Employee</option>
                <option value="Manager">Manager</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
          </div>

          {/* User Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredUsers.map((employee) => (
              <div
                key={employee.email}
                className="bg-white rounded-xl border border-border p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`user-card-${employee.email}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-heading font-semibold text-lg text-foreground truncate">
                          {employee.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {employee.role === 'Admin' ? 'Administrator' : 'Employee'}
                        </p>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                        {employee.role.toLowerCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{employee.email}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{employee.phone || '-'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>{employee.location || '-'}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4 flex-shrink-0" />
                    <span>Manager: {getManagerName(employee.manager)}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-border">
                  <button
                    onClick={() => handleEditUser(employee)}
                    data-testid={`edit-user-${employee.email}`}
                    className="flex-1 flex items-center justify-center gap-1 md:gap-2 px-3 md:px-4 py-2 border border-input rounded-lg text-xs md:text-sm font-medium text-foreground hover:bg-muted transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                    <span className="hidden md:inline">Edit</span>
                  </button>
                  <button
                    onClick={() => handleDeleteUser(employee.email, employee.name)}
                    data-testid={`delete-user-${employee.email}`}
                    className="flex items-center justify-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-red-600 text-white rounded-lg text-xs md:text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden md:inline">Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No users found</p>
            </div>
          )}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground">Create New Employee</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="create-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  data-testid="create-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  data-testid="create-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Phone Number
                </label>
                <input
                  type="text"
                  data-testid="create-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Designation
                </label>
                <input
                  type="text"
                  data-testid="create-designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Senior Developer"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="create-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Office">Office (10 paid leaves)</option>
                  <option value="Remote">Remote (8 paid leaves)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Manager
                </label>
                <select
                  data-testid="create-manager"
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">No Manager</option>
                  {getManagersList().map((manager) => (
                    <option key={manager.email} value={manager.email}>
                      {manager.name} ({manager.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="create-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Employee">Employee</option>
                  <option value="Manager">Manager</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  data-testid="submit-create"
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-6 py-3 font-heading font-semibold transition-colors"
                >
                  Create Employee
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-3 border border-input rounded-lg text-foreground hover:bg-muted transition-colors font-heading font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground">Edit Employee</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  disabled
                  value={formData.email}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-muted text-sm text-muted-foreground cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Phone Number
                </label>
                <input
                  type="text"
                  data-testid="edit-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Designation
                </label>
                <input
                  type="text"
                  data-testid="edit-designation"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Senior Developer"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Location <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="edit-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Office">Office (10 paid leaves)</option>
                  <option value="Remote">Remote (8 paid leaves)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Manager
                </label>
                <select
                  data-testid="edit-manager"
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">No Manager</option>
                  {getManagersList().map((manager) => (
                    <option key={manager.email} value={manager.email}>
                      {manager.name} ({manager.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  New Password <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  type="password"
                  data-testid="edit-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Leave blank to keep current password"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  data-testid="edit-role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="Employee">Employee</option>
                  <option value="Manager">Manager</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  data-testid="submit-edit"
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-6 py-3 font-heading font-semibold transition-colors"
                >
                  Update Employee
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-3 border border-input rounded-lg text-foreground hover:bg-muted transition-colors font-heading font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
